import useOnDragStart from '@components/ArtBoard/DragAndDrop/hooks/useOnDragStart'
import { DRAGGABLE_TYPE } from '@components/ArtBoard/DragAndDrop/types'
import { useIsMobile } from '@lib/hooks'
import {
  selectNodeChildren,
  selectNodeParent,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import cx from 'classnames'
import type React from 'react'
import { shallowEqual, useSelector } from 'react-redux'

import { TREE_DEFAULT, TREE_MATRIX } from '@/constants/constants'
import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { useArtboardMode } from '@/hooks'
import type { AppState } from '@/types'
import { hexToRgba } from '@/utils/colors'
import { Input } from './components/Input'
import css from './NodeGhost.module.scss'

type Props = {
  id: string
  isOverlay: boolean
  canDrag: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
}

const NodeGhost: React.FC<Props> = ({
  id,
  isOverlay = false,
  canDrag,
  containerRef,
}) => {
  const node = useSelector(migrationProjectSelector.getProjectNodeById(id))
  const projectTreeMode = useSelector(
    migrationProjectSelector.getProjectTreeType
  )

  const { title: name, collapsed: isCollapsed, colorId } = node

  const colorHexUI = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const childIds = useSelector(selectNodeChildren(id), shallowEqual)

  const parentIds = useSelector(selectNodeParent(id), shallowEqual)
  const parentId = parentIds ? parentIds[0] : null

  const onDragStart = useOnDragStart(containerRef, DRAGGABLE_TYPE.PAGE, id, {})

  const isDragging = useSelector<AppState, boolean>(
    ({ artboard }) => !isOverlay && id === artboard.dragAndDrop.dragged.id
  )

  const isOver = useSelector<AppState, boolean>(
    ({ artboard }) =>
      id === artboard.dragAndDrop.dropZone.id &&
      id !== artboard.dragAndDrop.dragged.id
  )

  // The title input may already have focus when the artboard goes off.
  const isEditable = useSelector<AppState, boolean>(
    ({ canvas, artboard }) =>
      !artboard.isDisabled && canvas.node.selectedToEditNode === id
  )

  const isMultipleSelected = useSelector<AppState, boolean>(({ canvas }) =>
    canvas.node.selectedNodes.includes(id)
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const isMobile = useIsMobile()
  const { symbolLibrary, estimate, readonlyEstimate, gallery, seoMode } =
    useArtboardMode()

  const nodeStyles = {
    '--node-color': colorHexUI,
    '--node-select-color': hexToRgba(colorHexUI, 0.25),
  } as React.CSSProperties

  const isChild = parentId !== null
  const hasChild = childIds.length === 1
  const hasChildren = childIds.length > 1

  const nodeClasses = {
    node: cx(css.node, {
      [css.isChild]: isChild,
      [css.hasChild]: hasChild && !isCollapsed,
      [css.hasChildren]: hasChildren && !isCollapsed,
      [css.map]: projectTreeMode === TREE_DEFAULT && !isOverlay,
      [css.matrix]: projectTreeMode === TREE_MATRIX && !isOverlay,
      [css.selected]: isMultipleSelected,
      [css.active]: isEditable,
      [css.dragging]: isDragging,
      [css.mobile]: isMobile,
      [css.over]: isOver,
      [css.overlay]: isOverlay,
    }),
    name: cx('Tree-node-ghost-name', css.name, {
      [css.readOnly]: isReadOnly,
      [css.symbolsLibrary]: symbolLibrary,
      [css.estimate]: estimate || readonlyEstimate,
      [css.gallery]: gallery,
      [css.seo_mode]: seoMode,
    }),
  }

  return (
    <div
      id={id}
      className={nodeClasses.node}
      style={nodeStyles}
      onPointerDown={canDrag ? onDragStart : undefined}
    >
      {isEditable && !isReadOnly && <Input name={name} id={id} />}

      {!isEditable && (
        <div className={nodeClasses.name}>
          {name === ' ' ? '\u00A0' : name || DEFAULT_PAGE_NAME}
        </div>
      )}
    </div>
  )
}

export default NodeGhost
